import { useChat } from "@/contexts/ChatContext";

/**
 * The model's follow-up chips, under the latest answer (D-09, D-12, WEB-03).
 *
 * THE SAME CHIP AS THE EMPTY STATE, ON PURPOSE. The pill treatment, the 44px target, the disabled
 * treatment and the focus ring are `ChatEmptyState`'s verbatim: a visitor should not be able to
 * tell a template chip from a model chip by looking at one, because the two do exactly the same
 * thing when tapped. Three things differ, and all are deliberate:
 *
 *   - The ROW is a single line that scrolls horizontally (39-08 checkpoint feedback). A vertical
 *     stack under every answer eats the message list; content-width pills in one overflow-x row
 *     cost one line of height however many chips arrive. Zero scroll offset under the panel's
 *     inherited `dir="rtl"` already IS the inline start, so the row never sets `scrollLeft` — the
 *     same rule `ChatStoreCarousel` states for its cards.
 *   - The row's top margin is smaller. These sit under an ANSWER inside a conversation, not under a
 *     greeting in an otherwise empty panel, so they belong to the bubble above them.
 *   - The key is the index rather than a chip id, because a model label has no stable id. That is
 *     safe for exactly as long as it needs to be: the row is replaced wholesale on every turn and is
 *     never reordered, filtered or appended to in place.
 *
 * IT RETURNS NOTHING ON AN EMPTY ARRAY. That is what lets the call site be unconditional: on a
 * normal turn this component contributes no DOM at all rather than an empty wrapper that would still
 * take the parent's flex gap.
 *
 * ─── THE LABELS ARE MODEL OUTPUT ──────────────────────────────────────────────
 *
 * They render as React children and nothing else. No raw-markup escape hatch is used anywhere in
 * this panel (T-38-86), and React's own escaping is the whole mitigation for T-39-35 — which matters
 * here more than almost anywhere, because after the answer body itself this is the most
 * attacker-adjacent string the panel draws. Nor does the row steal focus when it appears: the
 * visitor may be mid-sentence in the composer, and a row of buttons arriving under their cursor must
 * not take it.
 *
 * ─── TWO ACCEPTED COSTS, SO A LATER READER DOES NOT READ THEM AS BUGS ─────────
 *
 * 1. Chips are NOT persisted (D-09). A rehydrated thread shows the answer text with no chips under
 *    it. Persisting them would mean inventing a value for every historical turn and leaving stale
 *    follow-ups in scrollback, offering to continue a conversation that has already moved on.
 * 2. These labels bypass `__tests__/chatCopy.test.ts`'s no-dash lint entirely. That suite walks
 *    MODULE EXPORTS, and these strings arrive over the wire, so there is nothing for it to walk. The
 *    enforcement lives on the server instead: `suggest_replies`'s executor drops any label carrying
 *    an em or en dash, backed by the prompt rule telling the model not to write one.
 */
export default function ChatSuggestedReplies() {
  const { suggestedReplies, sendMessage, composerLocked } = useChat();

  if (suggestedReplies.length === 0) return null;

  return (
    // D-12: a tapped chip sends its label as an ORDINARY user message. That is already how a tapped
    // Meta quick reply behaves in practice, since nothing in the repo reads a quick reply's payload
    // and there is no postback handler, and it is what the empty-state chips already do. No payload
    // vocabulary is introduced here, and none should be.
    //
    // `sendMessage` owns the capture, per this panel's rule that every capture lives in ChatContext.
    // It receives a fixed synthetic id plus the position and NEVER the Arabic label: model chips have
    // no stable id by construction, so the kind-plus-position pair is the join key that survives any
    // copy change, because there is no copy in it to change.
    // `py-1` exists for the focus ring: `ring-offset-2` draws outside the button, and an
    // overflow container with no block padding clips the ring's top and bottom edges.
    <div
      className="mt-2 flex gap-2 overflow-x-auto py-1"
      style={{ overscrollBehaviorX: "contain", scrollbarWidth: "none" }}
    >
      {suggestedReplies.map((label, index) => (
        <button
          key={index}
          type="button"
          disabled={composerLocked}
          onClick={() => sendMessage({ text: label, chipId: "model_suggested", chipPosition: index })}
          className="flex min-h-11 shrink-0 items-center whitespace-nowrap rounded-[var(--ys-r-pill)] border border-[var(--ys-rule)] bg-[var(--ys-bg-2)] px-4 py-2 transition-colors hover:bg-[var(--ys-primary-soft)] disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ys-ink-2)] focus-visible:ring-offset-2"
          style={{ fontSize: 13, fontWeight: 400, lineHeight: 1.5, color: "var(--ys-ink-2)" }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
